import * as skylineApi from "./skylineApi"
import PersistentWebSocket from "./persistentWebSocket"

export const EventTypes = {
  INDEX_JUMP:"handleMissionIndexJump",
  STEP:"handleMissionStep",
  HISTORY_LOAD:"handleMissionHistoryLoad",
  HISTORY_APPEND:"handleMissionHistoryAppend",
  SYNC_COMPLETED:"handleSyncCompleted"
}

export class MissionEventHandler {
  constructor(mission) {
    this.mission = mission
    this.mission.registerEventHandler(this)
  }
  handleMissionIndexJump(targetIndex) { }
  handleMissionStep(message) { }
  handleMissionHistoryLoad() { }
  /**
   * Called when a new message is put in the mission history.
   * @param {MissionHistoryMessage} message The newly appended message
   */
  handleMissionHistoryAppend(message) { }
  handleSyncCompleted(message) {}

  cleanup() {
    this.mission.removeEventHandler(this)
  }
}

export class Mission {
  serverState = {} // The actual information from the server
  history = [] // Main history
  eventHandlers = []
  replayInfo = {
    speed: 1,
    prevFrameTime: 0,
    accumulationStart: 0,
    currentIndex: 0,
    animationHandle: null,
    elapsed: 0,
    isPaused: false,
  }
  initialSync = null

  constructor(serverState) {
    this.serverState = serverState
    this.historyPromise = this.getHistory()
    if (this.isLive) {
      this.connect()
    }
  }

  async startup() {
    if (!this.historyPromise) return
    return this.historyPromise
  }

  get isLive() {
    return this.serverState.eventWebSocket !== undefined
  }

  get lastHistoryIndex() {
    return this.history.length - 1
  }

  get isReplaying() {
    return this.replayInfo.currentIndex < this.lastHistoryIndex
  }

  get isReplayPaused() {
    return this.replayInfo.isPaused
  }
  set isReplayPaused(value) {
    this.replayInfo.isPaused = value
    this.startStepping()
  }

  get isConnected() {
    return this.liveWs
  }
  
  connect() {
    if (!this.isLive) {
      console.error("Tried to connect to an ended mission.")
      return
    }

    if (this.liveWs) {
      return
    }

    // Connect to the websocket for live data
    this.liveWs = new PersistentWebSocket(this.serverState.eventWebSocket)
    this.liveWs.addEventListener("message", this.handleLiveWsMessage.bind(this))
    this.liveWs.connect()

    document.body.classList.add('missionFeedConnected')
  }

  disconnect() {
    this.liveWs?.close()
    delete this.liveWs
    document.body.classList.remove('missionFeedConnected')
  }

  registerEventHandler(handler) {
    for (const fn of Object.values(EventTypes)) {
      if (!handler[fn] || typeof handler[fn] !== "function") {
        console.error("Can not register the handler because it does not have the function ", fn)
        return
      }
    }
    this.eventHandlers.push(handler)
  }

  removeEventHandler(handler) {
    this.eventHandlers.slice(this.eventHandlers.indexOf(handler), 1)
  }

  updateServerState(state) {
    this.serverState = state
    this.dispatchEvent(EventTypes.SYNC_COMPLETED)
  }

  dispatchEvent(eventType, data) {
    console.assert(eventType)
    for (const handler of this.eventHandlers) {
      try {
        handler[eventType](data)
      } catch(err) {
        console.error(`Handler ${typeof handler}.${eventType} threw an error:`, err)
      }
    }
  }

  handleLiveWsMessage(event) {
    // Display stubbed data if we disconnect
    if (event && event.detail) {
      let rawMsg
      try {
        rawMsg = JSON.parse(event.detail);
      } catch (err) {
        console.error("Got Invalid JSON on the live feed.", event)
        return
      }
      if (rawMsg.type == "MISSION_STATE") {
        console.log("Updating state", rawMsg)
        // TODO:  Is this best?
        if (rawMsg.state === "ENDED" && this.isLive) {
          this.disconnect()
        }
        this.serverState = rawMsg.mission
      } else if (rawMsg.type == "MISSION_SYNC") {
        this.serverState = rawMsg.mission
        this.initialSync = rawMsg
        this.dispatchEvent(EventTypes.SYNC_COMPLETED, rawMsg)
        return
      }
      let skipDispatch = this.isReplaying || this.isReplayPaused
      this.history.push(rawMsg)
      this.dispatchEvent(EventTypes.HISTORY_APPEND, rawMsg)
      if (skipDispatch) return
      this.replayInfo.currentIndex = this.history.length - 1
      this.dispatchEvent(EventTypes.STEP, rawMsg)
    }
  }
  
  addStatsHistory(entry) {
    if (!entry || !entry.deviceId) {
      return
    }
    let stats = this.stats[entry.deviceId]
    if (stats == undefined) {
      stats = []
      stats[entry.deviceId] = stats
    }
    if (stats.length >= HISTORY_BACKLOG) {
      stats.shift()
    }
    stats.push(entry)
  }

  async getHistory() {
    console.assert((this.serverState && this.serverState.id), "Tried to get history on an improper mission", this.serverState)
    if (this.serverState.state === "CREATED") return
    this.historyPromise = skylineApi.getMissionLog(this.serverState.id)
    this.historyPromise.then( (messages) => {
      // Dedupe out the overlap before concat
      if (this.history.length > 0) {
        const firstTimestamp = this.history[0].timestamp
        let i = messages.length - 1
        while (i > 0 && messages[i].timestamp > firstTimestamp) {
          i--
        }
        if (i < (messages.length - 1)) {
          console.log("Removing overlap from the history", messages, i)
          messages.splice(i)
        }

        // Smash the history together
        console.log("Combining the log and live data", messages[messages.length - 1], this.history[0])
        this.history = messages.concat(this.history)
      } else {
        this.history = messages
      }

      console.log("Moving forward the index " , this.replayInfo.currentIndex, messages.length, this.history.length)
      this.replayInfo.currentIndex += messages.length

      delete this.historyPromise

      this.dispatchEvent(EventTypes.HISTORY_LOAD)
    })
  }
  
  startReplay() {
    this.replayInfo.isPaused = false
    this.setReplayIndex(0)
  }

  startStepping() {
    if (this.replayInfo.animationHandle) {
      cancelAnimationFrame(this.replayInfo.animationHandle)
    }
    this.replayInfo.prevFrameTime = 0

    if (!this.isReplayPaused) {
      this.replayInfo.animationHandle = window.requestAnimationFrame(this.stepMessages.bind(this))
    }
  }

  setReplayIndex(index) {
    this.replayInfo.currentIndex = index || 0

    this.dispatchEvent(EventTypes.INDEX_JUMP, this.replayInfo.currentIndex)

    this.startStepping()
  }

  stopReplay() {
    console.log('stopping replay')
    if (this.replayInfo.animationHandle) window.cancelAnimationFrame(this.replayInfo.animationHandle)
    this.replayInfo.currentIndex = this.history.length - 1
    this.replayInfo.stopped = true
  }

  setReplaySpeed(speed) {
    window.cancelAnimationFrame(this.replayInfo.animationHandle)
    this.replayInfo.animationHandle = null
  
    this.replayInfo.speed = speed
    if (this.replayInfo.speed < .25) {
      this.replayInfo.speed = .25
    }
  
    if (!this.isReplayPaused) {
      this.replayInfo.animationHandle = window.requestAnimationFrame(this.stepMessages.bind(this))
    } else {
      this.replayInfo.prevFrameTime = 0
    }
  }
  
  stepMessages(timestamp) {
    if (!this.isReplaying || this.isReplayPaused || this.replayInfo.stopped ) {
      return
    }
  
    if (this.replayInfo.prevFrameTime === 0) {
      // Do our initial setup
      this.replayInfo.prevFrameTime = timestamp
      this.replayInfo.accumulationStart = this.history[this.replayInfo.currentIndex].timestamp
      this.replayInfo.elapsed = 0
    }
  
    const frameTime = timestamp - this.replayInfo.prevFrameTime
    this.replayInfo.prevFrameTime = timestamp
    
    this.replayInfo.elapsed += frameTime * this.replayInfo.speed
  
    let lastTimestampToSend = this.replayInfo.accumulationStart + this.replayInfo.elapsed
  
    while (this.replayInfo.currentIndex <  this.history.length && this.history[this.replayInfo.currentIndex].timestamp <= lastTimestampToSend) {
      this.replayInfo.currentIndex++
      this.dispatchEvent(EventTypes.STEP, this.history[this.replayInfo.currentIndex-1])
    }
  
    this.replayInfo.animationHandle = window.requestAnimationFrame(this.stepMessages.bind(this))
  }

  cleanup() {
    this.disconnect()
    this.stopReplay()
  }
}